import type { FiatPrice } from "@b2bportal/core-types";
import type { HotelItinerary, Lodging } from "@b2bportal/lodging-api";
import { calculatePointsEarnedForFiat } from "./calculatePointsEarnedForFiat";

export const getAwardableAmountForLodging = (
  lodging: Lodging
): FiatPrice | undefined => {
  if (lodging.price?.tripTotalExcludingTaxes?.fiat) {
    return lodging.price.tripTotalExcludingTaxes.fiat;
  }

  const totalPrice = lodging.price?.totalPrice?.fiat;
  const totalTax = lodging.price?.taxBreakdown?.baseRateTax?.fiat;

  if (totalPrice && totalTax) {
    return {
      ...totalPrice,
      value: totalPrice.value - totalTax.value,
    };
  }

  return undefined;
};

export const calculatePointsEarnedForLodging = (lodging: Lodging): number => {
  const awardableAmount = getAwardableAmountForLodging(lodging);
  if (awardableAmount == null) return 0;
  return calculatePointsEarnedForFiat(awardableAmount, "hotel");
};

/**
 * Calculates points earned from a hotel itinerary, accounting for split payments.
 * When points are used to pay for part of the booking, earn is only calculated
 * on the fiat (card) payment portion, excluding taxes.
 *
 * This ensures consistency with the checkout flow where earn is calculated as:
 * earnableAmount = sellRate - pointsAppliedAsFiat
 * (sellRate doesn't include taxes)
 */
export const calculatePointsEarnedFromItinerary = (
  itinerary: HotelItinerary
): number => {
  const cardPayment = itinerary.paymentBreakdown.paymentLineItems.find(
    (item) => item.type === "Card"
  )?.value.paymentAmount;

  if (!cardPayment) return 0;

  const taxes = itinerary.reservation.pricing.taxes?.fiat?.value ?? 0;
  const earnableAmount = Math.max(0, cardPayment.amount - taxes);

  return calculatePointsEarnedForFiat(
    {
      value: earnableAmount,
      currencyCode: cardPayment.currency,
      currencySymbol: "$",
    },
    "hotel"
  );
};
